import React from 'react';
import { render, fireEvent, waitFor } from '@testing-library/react';

import New from './New';

// ── Mocks ──────────────────────────────────────────────────────────────────────

// Drives the component the way a click does, so no test reaches into internals.
jest.mock('lib/admin-kit/hooks/useAdminMutation', () => (opts) => ({
  isSubmitting: false,
  mutate: (...args) => {
    global.__mutateCalls.push(args);
    if (global.__runResponse) opts.onSuccess(global.__runResponse);
    if (global.__mutateError) opts.onError(global.__mutateError);
  },
}));

jest.mock('lib/admin-kit/components/AdminPage', () => ({ title, children }) => (
  <div>
    <h1>{title}</h1>
    {children}
  </div>
));
jest.mock('lib/admin-kit/components/AdminPanel', () => ({ title, children }) => (
  <section>
    <h2>{title}</h2>
    {children}
  </section>
));
jest.mock('lib/admin-kit/components/ErrorBox', () => ({ errors }) => (
  <div>{errors.map((e) => <span key={e}>{e}</span>)}</div>
));

jest.mock('@decisiv/ui-components', () => ({
  Button: ({ text, onClick, disabled }) => (
    <button type="button" onClick={onClick} disabled={disabled}>{text}</button>
  ),
  Flex: ({ children }) => <div>{children}</div>,
}));
jest.mock('@decisiv/ui-components/lib/components/Typography', () => ({
  H2: ({ children }) => <h3>{children}</h3>,
  P: ({ children }) => <p>{children}</p>,
}));

const RUN = { run_id: 'run-1', total: 3 };

const FINISHED = {
  total: 3,
  finished: true,
  results: [
    { email: 'invited@example.com', outcome: 'invited' },
    { email: 'missing@example.com', outcome: 'not_found' },
    { email: 'broken@example.com', outcome: 'failed', message: 'create-invitation failed: 400' },
  ],
};

const IN_PROGRESS = { total: 3, finished: false, results: [] };

const submit = (page, value = 'a@example.com, b@example.com') => {
  fireEvent.change(page.getByLabelText('Email addresses'), { target: { value } });
  fireEvent.click(page.getByText('Send Invitations'));
};

describe('PcarazInvitationsNew', () => {
  // Spies rather than advancing a clock: jest 27's modern fake timers also fake
  // the microtask queue, which deadlocks the awaited polling promise.
  let clearIntervalSpy;

  beforeEach(() => {
    global.__mutateCalls = [];
    global.__runResponse = null;
    global.__mutateError = null;
    window.fetch = jest.fn();
    jest.spyOn(window, 'setInterval').mockReturnValue(123);
    clearIntervalSpy = jest.spyOn(window, 'clearInterval').mockImplementation(() => {});
  });

  afterEach(() => {
    jest.restoreAllMocks();
  });

  it('renders the textarea and the submit button', () => {
    const page = render(<New />);

    expect(page.getByLabelText('Email addresses')).toBeTruthy();
    expect(page.getByText('Send Invitations')).toBeTruthy();
  });

  it('submits the pasted addresses', () => {
    const page = render(<New />);

    submit(page);

    expect(global.__mutateCalls).toEqual([[
      '/internal_admin/admin_kit/pcaraz_invitations',
      { method: 'POST', body: { emails: 'a@example.com, b@example.com' } },
    ]]);
  });

  it('shows the three outcome groups with their addresses once a run finishes', async () => {
    global.__runResponse = RUN;
    window.fetch.mockResolvedValue({ ok: true, json: async () => FINISHED });

    const page = render(<New />);
    submit(page);

    await waitFor(() => expect(page.getByText('Invited (1)')).toBeTruthy());

    expect(page.getByText('No matching user (1)')).toBeTruthy();
    expect(page.getByText('Failed (1)')).toBeTruthy();
    expect(page.getByText('invited@example.com')).toBeTruthy();
    expect(page.getByText('missing@example.com')).toBeTruthy();
    expect(page.getByText(/create-invitation failed: 400/)).toBeTruthy();
  });

  it('reports progress while the run is still going', async () => {
    global.__runResponse = RUN;
    window.fetch.mockResolvedValue({ ok: true, json: async () => IN_PROGRESS });

    const page = render(<New />);
    submit(page);

    // Progress copy only: react-dom is pinned at 16.8 with no awaitable act(), so
    // RTL's fallback runs the effect cleanup during waitFor, making "the interval
    // is still live" untestable here. The two tests below cover the cleanup.
    await waitFor(() => expect(page.getByText(/Working/)).toBeTruthy());
  });

  it('clears the interval once the run reports finished', async () => {
    global.__runResponse = RUN;
    window.fetch.mockResolvedValue({ ok: true, json: async () => FINISHED });

    const page = render(<New />);
    submit(page);

    await waitFor(() => expect(clearIntervalSpy).toHaveBeenCalledWith(123));
  });

  it('clears the interval on unmount, so a closed page stops polling', async () => {
    global.__runResponse = RUN;
    window.fetch.mockResolvedValue({ ok: true, json: async () => IN_PROGRESS });

    const page = render(<New />);
    submit(page);

    await waitFor(() => expect(page.getByText(/Working/)).toBeTruthy());
    page.unmount();

    expect(clearIntervalSpy).toHaveBeenCalledWith(123);
  });

  it('reports an expired run instead of polling forever', async () => {
    global.__runResponse = RUN;
    window.fetch.mockResolvedValue({ ok: false, status: 404 });

    const page = render(<New />);
    submit(page);

    await waitFor(() => expect(page.getByText(/no longer available/)).toBeTruthy());
    expect(clearIntervalSpy).toHaveBeenCalledWith(123);
  });

  it('does not call a lapsed session an expired run', async () => {
    global.__runResponse = RUN;
    window.fetch.mockResolvedValue({ ok: false, status: 403 });

    const page = render(<New />);
    submit(page);

    await waitFor(() => expect(page.getByText(/HTTP 403/)).toBeTruthy());

    expect(page.getByText(/may still be sending/)).toBeTruthy();
    expect(page.queryByText(/no longer available/)).toBeNull();
    expect(clearIntervalSpy).toHaveBeenCalledWith(123);
  });

  it('reports a server error distinctly from an expired run', async () => {
    global.__runResponse = RUN;
    window.fetch.mockResolvedValue({ ok: false, status: 500 });

    const page = render(<New />);
    submit(page);

    await waitFor(() => expect(page.getByText(/HTTP 500/)).toBeTruthy());
    expect(page.queryByText(/no longer available/)).toBeNull();
  });

  it('handles an unreachable server instead of rejecting on every tick', async () => {
    global.__runResponse = RUN;
    window.fetch.mockRejectedValue(new TypeError('Failed to fetch'));

    const page = render(<New />);
    submit(page);

    await waitFor(() => expect(page.getByText(/Could not reach the server/)).toBeTruthy());

    expect(page.getByText(/may still be sending/)).toBeTruthy();
    expect(clearIntervalSpy).toHaveBeenCalledWith(123);
  });

  it('shows the error when the submission is rejected', () => {
    global.__mutateError = new Error(JSON.stringify({ errors: ['Enter at least one email address.'] }));

    const page = render(<New />);
    submit(page, '   ');

    expect(page.getByText('Enter at least one email address.')).toBeTruthy();
  });
});
